import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Card } from './Card';
import { Notch, type NotchAction } from './Notch';
import s from './Kpi.module.css';

type KpiProps = {
  eyebrow: ReactNode;
  title: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'acc' | 'err';
  sub?: ReactNode;
  notch?: NotchAction[];
  onClick?: () => void;
};

/** Dashboard KPI: eyebrow, title, big value, delta pill, sub-line; optional Notch; clickable. */
export function Kpi({ eyebrow, title, value, delta, deltaTone = 'acc', sub, notch, onClick }: KpiProps) {
  return (
    <Card texture="dots" relative lift={!!onClick} onClick={onClick} className={s.kpi}>
      {notch && <Notch actions={notch} />}
      <div className={s.head}>
        <span className={s.eyebrow}>{eyebrow}</span>
        <span className={s.title}>{title}</span>
      </div>
      <div className={s.valueRow}>
        <span className={s.value}>{value}</span>
        {delta && <span className={cx(s.delta, deltaTone === 'err' && s.deltaErr)}>{delta}</span>}
      </div>
      {sub && <span className={s.sub}>{sub}</span>}
    </Card>
  );
}

type StatProps = {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  sub?: ReactNode;
  /** sub-line colour: default #85858B; muted #A1A1A6; accent #FF9A57; ok green */
  subTone?: 'default' | 'muted' | 'accent' | 'ok';
  /** 44px KPI value (plans) or a 30px tile value (site overview). */
  size?: 'kpi' | 'tile';
};

/** A stat card: label, value (+ unit), sub-line. Used by the plans KPIs and the site overview tiles. */
export function StatCard({ label, value, unit, sub, subTone = 'default', size = 'kpi' }: StatProps) {
  return (
    <Card padding={20} className={s.stat}>
      <span className={s.label}>{label}</span>
      {size === 'kpi' ? (
        <div className={s.statValueRow}>
          <span className={s.statValue}>{value}</span>
          {unit && <span className={s.unit}>{unit}</span>}
        </div>
      ) : (
        <span className={s.tileValue}>{value}</span>
      )}
      {sub && <span className={cx(s.statSub, subTone !== 'default' && s[subTone])}>{sub}</span>}
    </Card>
  );
}
